// src/pages/DataPolicy.tsx
//
// Standalone Data & Privacy Policy page for Launch Kit Naija / adeoluadegbemi.com
// Covers: data collected, Meta Pixel, Paystack, email marketing, user rights, contact
//
// Usage in HomeRoutes.tsx:
//   import DataPolicy from "../pages/DataPolicy";
//   <Route path="/data-policy" element={<HomeLayout><DataPolicy /></HomeLayout>} />
//
// Also update the footer link in DemoRequestPage.tsx if you want to point
// specifically to this page instead of the generic /privacy route.

import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ─── Section heading helper ────────────────────────────────────────────────────
const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-12 mb-4 pb-2 border-b-2 border-blue-100">
    {children}
  </h2>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mt-6 mb-2">
    {children}
  </h3>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-gray-600 leading-relaxed mb-4">{children}</p>
);

// ─── Component ─────────────────────────────────────────────────────────────────
const DataPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const lastUpdated = "27 February 2026";
  const contactEmail = "hello@adeoluadegbemi.com";
  const brandName = "Launch Kit Naija";
  const operatorName = "Adeolu Adegbemi";
  const websiteUrl = "https://www.adeoluadegbemi.com";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero banner ── */}
      <div className="bg-gradient-to-r from-blue-900 to-brandNavy text-white py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 bg-blue-800 text-blue-200 text-xs font-semibold rounded-lg mb-4 uppercase tracking-wide">
            Legal
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">
            Data & Privacy Policy
          </h1>
          <p className="text-blue-200 text-sm sm:text-base max-w-xl mx-auto">
            This policy explains what data we collect, why we collect it, how we
            use it, and what rights you have over your information.
          </p>
          <p className="text-blue-300 text-xs mt-4">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* ── Body ── */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          {/* Quick nav */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-10">
            <p className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-3">
              Contents
            </p>
            <ol className="space-y-1.5 text-sm text-blue-700">
              {[
                ["#who-we-are", "1. Who We Are"],
                ["#what-we-collect", "2. What Data We Collect"],
                ["#how-we-collect", "3. How We Collect It"],
                ["#meta-pixel", "4. Meta Pixel & Facebook Advertising"],
                ["#paystack", "5. Paystack Payment Processing"],
                ["#email", "6. Email Marketing & Communications"],
                ["#how-we-use", "7. How We Use Your Data"],
                ["#sharing", "8. Who We Share Your Data With"],
                ["#retention", "9. How Long We Keep Your Data"],
                ["#your-rights", "10. Your Rights"],
                ["#cookies", "11. Cookies"],
                ["#security", "12. Data Security"],
                ["#children", "13. Children's Privacy"],
                ["#changes", "14. Changes to This Policy"],
                ["#contact", "15. Contact Us"],
              ].map(([href, label]) => (
                <li key={href}>
                  <a
                    href={href}
                    className="hover:text-blue-900 hover:underline transition"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* ─────────────────────────────────────────
              1. WHO WE ARE
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="who-we-are">1. Who We Are</span>
          </SectionHeading>
          <Paragraph>
            This website ({websiteUrl}) is operated by {operatorName}, trading
            as <strong>{brandName}</strong>. When we say "we", "us", or "our" in
            this policy, we are referring to {operatorName} and the {brandName}{" "}
            product.
          </Paragraph>
          <Paragraph>
            {brandName} is a done-for-you sales system for Nigerian businesses.
            We build and deliver custom sales infrastructure including lead
            capture pages, automated email sequences, sales pages, and payment
            collection — all hosted on your own domain.
          </Paragraph>
          <Paragraph>
            For any data-related enquiries, contact us at:{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              {contactEmail}
            </a>
          </Paragraph>

          {/* ─────────────────────────────────────────
              2. WHAT WE COLLECT
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="what-we-collect">2. What Data We Collect</span>
          </SectionHeading>

          <SubHeading>2a. Information you provide directly</SubHeading>
          <Paragraph>
            When you fill in any form on this website — including demo access
            requests, purchase forms, or contact forms — we collect the
            information you submit. This typically includes your first name,
            email address, and WhatsApp phone number. For purchases, we also
            collect your billing details (processed securely through Paystack —
            see Section 5).
          </Paragraph>

          <SubHeading>2b. Information collected automatically</SubHeading>
          <Paragraph>
            When you visit this website, we automatically collect certain
            technical information. This includes your IP address, browser type
            and version, operating system, referring URL, pages visited, time
            spent on pages, and the date and time of your visit. This data is
            collected via cookies, the Meta Pixel, and standard web server logs.
          </Paragraph>

          <SubHeading>2c. Behavioural & engagement data</SubHeading>
          <Paragraph>
            We track which emails you open, which links you click in those
            emails, which pages you visit after clicking, and which forms you
            submit. This helps us understand what content is useful to you and
            send more relevant follow-up communications.
          </Paragraph>

          {/* ─────────────────────────────────────────
              3. HOW WE COLLECT IT
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="how-we-collect">3. How We Collect It</span>
          </SectionHeading>
          <Paragraph>We collect data through the following means:</Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              <strong>Forms on this website</strong> — opt-in forms, demo
              request forms, payment forms, and contact forms.
            </li>
            <li>
              <strong>The Meta Pixel</strong> — a piece of JavaScript code
              embedded in this website that communicates with Meta's servers
              when you take certain actions (see Section 4).
            </li>
            <li>
              <strong>Paystack</strong> — our payment processor, which handles
              all card and bank transfer data on our behalf (see Section 5).
            </li>
            <li>
              <strong>Email tracking</strong> — our email platform records
              whether you open emails and click links.
            </li>
            <li>
              <strong>Web server logs</strong> — standard server-side logs that
              capture technical details of every request made to the site.
            </li>
          </ul>

          {/* ─────────────────────────────────────────
              4. META PIXEL
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="meta-pixel">
              4. Meta Pixel &amp; Facebook Advertising
            </span>
          </SectionHeading>
          <Paragraph>
            This website uses the <strong>Meta Pixel</strong> (formerly Facebook
            Pixel), a tool provided by Meta Platforms, Inc. The Meta Pixel is a
            snippet of JavaScript code that loads when you visit a page on this
            site. It enables us to:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              Measure the effectiveness of our Facebook and Instagram
              advertising campaigns by understanding what actions people take
              after seeing our ads.
            </li>
            <li>
              Build custom audiences of people who have visited this site, so we
              can show relevant follow-up advertisements on Facebook and
              Instagram.
            </li>
            <li>
              Optimise our ad delivery so that Meta shows our ads to people most
              likely to find them useful.
            </li>
          </ul>

          <SubHeading>What the Meta Pixel specifically tracks</SubHeading>
          <Paragraph>
            On this website, the Meta Pixel records the following standard
            events:
          </Paragraph>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Event
                  </th>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Triggered when
                  </th>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Pixel
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">
                    PageView
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Every page load across the site
                  </td>
                  <td className="px-4 py-3 text-gray-600">Both pixels</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">
                    ViewContent
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    When the Sales Page or Demo Request Page loads
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Sales Pixel or Demo Pixel respectively
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">
                    Lead
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    When you successfully register for the platform demo
                  </td>
                  <td className="px-4 py-3 text-gray-600">Demo Pixel only</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">
                    InitiateCheckout
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    When you click a Buy Now or payment button
                  </td>
                  <td className="px-4 py-3 text-gray-600">Sales Pixel only</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">
                    Purchase
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    When your payment is confirmed and you reach the success
                    page
                  </td>
                  <td className="px-4 py-3 text-gray-600">Sales Pixel only</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Paragraph>
            The data transmitted to Meta via the Pixel is governed by Meta's own{" "}
            <a
              href="https://www.facebook.com/privacy/policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              Data Policy
            </a>{" "}
            and{" "}
            <a
              href="https://www.facebook.com/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              Terms of Service
            </a>
            . This website is not affiliated with, endorsed by, or sponsored by
            Meta Platforms, Inc.
          </Paragraph>

          <SubHeading>Opting out of Meta tracking</SubHeading>
          <Paragraph>
            You can opt out of interest-based advertising by Meta by visiting{" "}
            <a
              href="https://www.facebook.com/ads/preferences"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              facebook.com/ads/preferences
            </a>{" "}
            or by using your browser's cookie settings to block the pixel (see
            Section 11).
          </Paragraph>

          {/* ─────────────────────────────────────────
              5. PAYSTACK
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="paystack">5. Paystack Payment Processing</span>
          </SectionHeading>
          <Paragraph>
            All payments on this website are processed by{" "}
            <strong>Paystack Payments Limited</strong>, a PCI-DSS compliant
            payment processor regulated in Nigeria. When you make a payment, you
            are redirected to Paystack's secure checkout environment. We do not
            store, see, or have access to your full card number, CVV, or bank
            PIN at any point.
          </Paragraph>
          <Paragraph>
            Paystack may collect your name, email address, card details, bank
            account details, IP address, and device information to process your
            payment and comply with applicable financial regulations. Their use
            of your data is governed by the{" "}
            <a
              href="https://paystack.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              Paystack Privacy Policy
            </a>
            .
          </Paragraph>
          <Paragraph>
            After a successful payment, we receive a confirmation from Paystack
            containing your email address, the amount paid, and a transaction
            reference. We use this to trigger product delivery and maintain our
            payment records.
          </Paragraph>

          {/* ─────────────────────────────────────────
              6. EMAIL MARKETING
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="email">6. Email Marketing &amp; Communications</span>
          </SectionHeading>
          <Paragraph>
            By submitting any form on this website, you consent to receive email
            communications from {brandName}. These may include:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              Transactional emails — order confirmations, product access links,
              payment receipts, and support responses.
            </li>
            <li>
              Follow-up sequences — automated email sequences designed to help
              you understand our product and decide if it is right for your
              business.
            </li>
            <li>
              Marketing emails — product announcements, promotional offers, and
              business growth content. These are sent only with your consent and
              always include an unsubscribe link.
            </li>
          </ul>
          <Paragraph>
            You can unsubscribe from marketing emails at any time by clicking
            the "Unsubscribe" link at the bottom of any email, or by sending a
            request to{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              {contactEmail}
            </a>
            . Unsubscribing from marketing emails does not affect transactional
            communications related to an active purchase.
          </Paragraph>

          {/* ─────────────────────────────────────────
              7. HOW WE USE YOUR DATA
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="how-we-use">7. How We Use Your Data</span>
          </SectionHeading>
          <Paragraph>
            We use the data we collect for the following purposes:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              <strong>To deliver your purchase</strong> — set up your system,
              grant platform access, and send onboarding materials.
            </li>
            <li>
              <strong>To communicate with you</strong> — send transactional
              emails, respond to enquiries, and provide customer support.
            </li>
            <li>
              <strong>To nurture leads</strong> — send automated follow-up
              sequences to people who have expressed interest in our product.
            </li>
            <li>
              <strong>To improve our marketing</strong> — measure ad
              performance, build retargeting audiences, and optimise our Meta ad
              campaigns.
            </li>
            <li>
              <strong>To improve our product</strong> — analyse which features
              and content resonate most so we can serve you better.
            </li>
            <li>
              <strong>To comply with legal obligations</strong> — maintain
              transaction records for tax, accounting, and regulatory purposes.
            </li>
          </ul>
          <Paragraph>
            We do not use your data for any automated decision-making that
            produces legal or similarly significant effects without human
            review.
          </Paragraph>

          {/* ─────────────────────────────────────────
              8. SHARING
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="sharing">8. Who We Share Your Data With</span>
          </SectionHeading>
          <Paragraph>
            We do not sell your personal data to any third party. We share data
            only with the following categories of service providers, and only to
            the extent necessary to operate our business:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              <strong>Paystack</strong> — to process your payment.
            </li>
            <li>
              <strong>Meta Platforms, Inc.</strong> — via the Meta Pixel, for ad
              measurement and audience building as described in Section 4.
            </li>
            <li>
              <strong>Email service providers</strong> — to send and track
              emails on our behalf. These providers are contractually bound to
              process your data only on our instructions.
            </li>
            <li>
              <strong>Hosting and infrastructure providers</strong> — the
              servers that store our application and your data. These providers
              operate under strict data processing agreements.
            </li>
          </ul>
          <Paragraph>
            We may also disclose your data if required by law, court order, or
            to protect the rights, property, or safety of our business, our
            customers, or the public.
          </Paragraph>

          {/* ─────────────────────────────────────────
              9. RETENTION
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="retention">9. How Long We Keep Your Data</span>
          </SectionHeading>
          <Paragraph>
            We retain your personal data for as long as is necessary to fulfil
            the purpose for which it was collected:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              <strong>Customer records</strong> — retained for a minimum of 7
              years after your last transaction to comply with Nigerian tax and
              accounting regulations (FIRS requirements).
            </li>
            <li>
              <strong>Marketing leads</strong> — retained until you unsubscribe
              or request deletion. After unsubscribing, your email is kept on a
              suppression list to prevent future accidental contact.
            </li>
            <li>
              <strong>Demo access records</strong> — retained for 12 months from
              registration, after which they are purged from active databases.
            </li>
            <li>
              <strong>Server logs</strong> — retained for 90 days then
              automatically deleted.
            </li>
          </ul>

          {/* ─────────────────────────────────────────
              10. YOUR RIGHTS
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="your-rights">10. Your Rights</span>
          </SectionHeading>
          <Paragraph>
            Under the Nigeria Data Protection Act 2023 (NDPA) and applicable
            international privacy standards, you have the following rights
            regarding your personal data:
          </Paragraph>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-2">
            <li>
              <strong>Right to access</strong> — you can request a copy of the
              personal data we hold about you.
            </li>
            <li>
              <strong>Right to rectification</strong> — you can ask us to
              correct inaccurate or incomplete data.
            </li>
            <li>
              <strong>Right to erasure</strong> — you can ask us to delete your
              data, subject to legal retention obligations.
            </li>
            <li>
              <strong>Right to restrict processing</strong> — you can ask us to
              limit how we use your data in certain circumstances.
            </li>
            <li>
              <strong>Right to data portability</strong> — you can request your
              data in a structured, machine-readable format.
            </li>
            <li>
              <strong>Right to object</strong> — you can object to our
              processing of your data for marketing purposes at any time.
            </li>
            <li>
              <strong>Right to withdraw consent</strong> — where processing is
              based on consent, you can withdraw it at any time without
              affecting the lawfulness of prior processing.
            </li>
          </ul>
          <Paragraph>
            To exercise any of these rights, email us at{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              {contactEmail}
            </a>{" "}
            with the subject line "Data Rights Request". We will respond within
            30 days.
          </Paragraph>

          {/* ─────────────────────────────────────────
              11. COOKIES
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="cookies">11. Cookies</span>
          </SectionHeading>
          <Paragraph>
            This website uses cookies — small text files stored on your device —
            to enable core functionality and improve your experience. The
            cookies we use fall into the following categories:
          </Paragraph>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Category
                  </th>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Purpose
                  </th>
                  <th className="text-left px-4 py-3 font-semibold border-b border-gray-200">
                    Can be disabled?
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    Strictly necessary
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Required for the site to function (e.g. session management,
                    payment processing)
                  </td>
                  <td className="px-4 py-3 text-gray-600">No</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    Analytics
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Helps us understand how visitors use the site
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Yes — via browser settings
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    Advertising (Meta Pixel)
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Tracks conversions and enables retargeting on Facebook and
                    Instagram
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Yes — via browser settings or Meta ad preferences
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Paragraph>
            To manage or disable cookies, you can adjust your browser settings.
            Most browsers allow you to refuse all cookies or alert you when a
            cookie is being sent. Note that disabling certain cookies may affect
            the functionality of this site.
          </Paragraph>

          {/* ─────────────────────────────────────────
              12. SECURITY
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="security">12. Data Security</span>
          </SectionHeading>
          <Paragraph>
            We take reasonable technical and organisational measures to protect
            your personal data against unauthorised access, loss, or
            destruction. These measures include HTTPS encryption on all pages,
            secure server infrastructure, access controls limiting who can view
            customer data, and use of PCI-DSS compliant payment processors.
          </Paragraph>
          <Paragraph>
            However, no method of transmission over the internet or electronic
            storage is 100% secure. While we strive to use commercially
            acceptable means to protect your data, we cannot guarantee its
            absolute security.
          </Paragraph>

          {/* ─────────────────────────────────────────
              13. CHILDREN
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="children">13. Children's Privacy</span>
          </SectionHeading>
          <Paragraph>
            This website and our services are intended for adults and business
            owners. We do not knowingly collect personal data from individuals
            under the age of 18. If we become aware that we have inadvertently
            collected data from a minor, we will delete it promptly. If you
            believe a minor has submitted data through this site, please contact
            us immediately at{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-blue-700 underline hover:text-blue-900 transition"
            >
              {contactEmail}
            </a>
            .
          </Paragraph>

          {/* ─────────────────────────────────────────
              14. CHANGES
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="changes">14. Changes to This Policy</span>
          </SectionHeading>
          <Paragraph>
            We may update this Data Policy from time to time to reflect changes
            in our practices, technology, legal requirements, or for other
            operational reasons. When we make material changes, we will update
            the "Last updated" date at the top of this page. We encourage you to
            review this policy periodically. Continued use of this website after
            changes are posted constitutes your acceptance of the updated
            policy.
          </Paragraph>

          {/* ─────────────────────────────────────────
              15. CONTACT
          ───────────────────────────────────────── */}
          <SectionHeading>
            <span id="contact">15. Contact Us</span>
          </SectionHeading>
          <Paragraph>
            If you have any questions, concerns, or requests regarding this Data
            Policy or the way we handle your personal data, please contact us:
          </Paragraph>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
            <p className="font-bold text-gray-900 mb-1">{operatorName}</p>
            <p className="text-gray-600 text-sm mb-1">
              Trading as: {brandName}
            </p>
            <p className="text-gray-600 text-sm mb-3">
              Website:{" "}
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-900 transition"
              >
                {websiteUrl}
              </a>
            </p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition"
            >
              📧 {contactEmail}
            </a>
          </div>
          <Paragraph>
            We will respond to all data-related enquiries within 30 days. For
            urgent matters, please include "URGENT — Data Request" in your
            subject line.
          </Paragraph>

          {/* ── Footer nav ── */}
          <div className="mt-12 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500">
            <p>Last updated: {lastUpdated}</p>
            <div className="flex items-center gap-4">
              <Link
                to="/privacy"
                className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition"
              >
                Terms of Service
              </Link>
              <Link
                to="/"
                className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition"
              >
                ← Home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DataPolicy;
